import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { invalidarZproHabilitado } from '../contatos/useZproHabilitado';

interface Props {
  /** Empresa do Admin. O SuperAdmin escolhe a empresa no seletor da própria tela. */
  empresaId?: string;
  isSuperAdmin?: boolean;
  /** Só SuperAdmin grava/testa; os demais apenas visualizam (senha sempre mascarada). */
  canEdit: boolean;
}

interface ZproConfig {
  enabled: boolean;
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  tenantId: string;
  mediaPath: string;
  sshPort: string;
  sshUser: string;
  sshPassword: string;
}

interface ZproTenant {
  id: number;
  nome: string;
  status: string;
  contatos: number;
  vinculadoA: string | null;
}

const vazio: ZproConfig = {
  enabled: false,
  host: '',
  port: '5432',
  database: '',
  user: '',
  password: '',
  tenantId: '',
  mediaPath: '',
  sshPort: '22',
  sshUser: '',
  sshPassword: '',
};

const ZproHistoricoConfig: React.FC<Props> = ({ empresaId: empresaIdProp, isSuperAdmin = false, canEdit }) => {
  const { toast } = useToast();
  const [empresas, setEmpresas] = useState<Array<{ id: string; nome: string }>>([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const empresaId = isSuperAdmin ? empresaSelecionada : empresaIdProp;
  const [config, setConfig] = useState<ZproConfig>(vazio);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resumo, setResumo] = useState<{
    tenant?: string | null;
    contatos: number;
    tickets: number;
    mensagens: number;
    midias?: { ok: boolean; mensagem: string };
  } | null>(null);
  const [tenants, setTenants] = useState<ZproTenant[] | null>(null);
  const [buscandoTenants, setBuscandoTenants] = useState(false);

  const carregar = useCallback(async () => {
    if (!empresaId) return;
    try {
      setLoading(true);
      const response = await api.get(`/empresas/${empresaId}/zpro`);
      const c = response?.data?.data;
      if (c) {
        setConfig({
          enabled: !!c.enabled,
          host: c.host || '',
          port: c.port ? String(c.port) : '5432',
          database: c.database || '',
          user: c.user || '',
          password: c.password || '',
          tenantId: c.tenantId ? String(c.tenantId) : '',
          mediaPath: c.mediaPath || '',
          sshPort: c.sshPort ? String(c.sshPort) : '22',
          sshUser: c.sshUser || '',
          sshPassword: c.sshPassword || '',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar configuração do Zpro',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [empresaId, toast]);

  // SuperAdmin: lista as empresas para escolher qual configurar
  useEffect(() => {
    if (!isSuperAdmin) return;
    api
      .get('/empresas', { params: { page: 1, pageSize: 500, sortBy: 'nome', sortOrder: 'asc' } })
      .then((r) => {
        const d = r?.data?.data || r?.data?.empresas || r?.data || [];
        const lista = (Array.isArray(d) ? d : [d]).filter((e: any) => e?.id).map((e: any) => ({ id: e.id, nome: e.nome }));
        setEmpresas(lista);
      })
      .catch(() =>
        toast({ title: 'Erro ao carregar empresas', description: 'Tente novamente', variant: 'destructive' })
      );
  }, [isSuperAdmin, toast]);

  useEffect(() => {
    setResumo(null);
    setTenants(null);
    setConfig(vazio);
    carregar();
  }, [carregar]);

  const payload = () => ({
    enabled: config.enabled,
    host: config.host.trim(),
    port: config.port ? Number(config.port) : null,
    database: config.database.trim(),
    user: config.user.trim(),
    password: config.password, // '***' mantém a senha salva
    tenantId: config.tenantId ? Number(config.tenantId) : null,
    mediaPath: config.mediaPath.trim(),
    sshPort: config.sshPort ? Number(config.sshPort) : null,
    sshUser: config.sshUser.trim(),
    sshPassword: config.sshPassword, // '***' mantém a senha salva
  });

  const salvar = async () => {
    try {
      setLoading(true);
      await api.put(`/empresas/${empresaId}/zpro`, payload());
      toast({ title: 'Sucesso', description: 'Configuração do Zpro salva com sucesso' });
      invalidarZproHabilitado();
      await carregar();
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const testar = async () => {
    try {
      setTesting(true);
      setResumo(null);
      const response = await api.post(`/empresas/${empresaId}/zpro/test`, payload());
      setResumo(response?.data?.data || null);
      toast({ title: 'Conexão OK', description: 'Conectado ao banco do Zpro (somente leitura)' });
    } catch (error: any) {
      toast({
        title: 'Falha na conexão',
        description: error?.response?.data?.message || 'Não foi possível conectar ao Zpro',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  const buscarTenants = async () => {
    try {
      setBuscandoTenants(true);
      const response = await api.post(`/empresas/${empresaId}/zpro/tenants`, payload());
      setTenants(response?.data?.data || []);
    } catch (error: any) {
      toast({
        title: 'Não foi possível listar os tenants',
        description: error?.response?.data?.message || 'Verifique host, banco, usuário e senha',
        variant: 'destructive',
      });
    } finally {
      setBuscandoTenants(false);
    }
  };

  const set = (campo: keyof ZproConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setConfig((prev) => ({ ...prev, [campo]: e.target.value }));

  const desabilitado = !canEdit || loading;
  const inputClass = 'mt-1 dark:bg-slate-700 dark:text-white';

  const seletorEmpresa = isSuperAdmin && (
    <div className="mb-5">
      <Label className="dark:text-white">Empresa</Label>
      <Select value={empresaSelecionada} onValueChange={setEmpresaSelecionada}>
        <SelectTrigger className="mt-1 dark:bg-slate-700 dark:text-white">
          <SelectValue placeholder="Selecione a empresa a configurar" />
        </SelectTrigger>
        <SelectContent className="dark:bg-slate-800">
          {empresas.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              {e.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  if (!empresaId) {
    return (
      <Card className="mb-6 p-4 dark:bg-slate-800">
        <h3 className="text-lg font-medium mb-2 text-gray-500 dark:text-white">Histórico do Zpro</h3>
        {seletorEmpresa}
        <p className="text-sm text-gray-500 dark:text-gray-400">Selecione uma empresa para configurar.</p>
      </Card>
    );
  }

  return (
    <Card className="mb-6 p-4 dark:bg-slate-800">
      <h3 className="text-lg font-medium mb-1 text-gray-500 dark:text-white">Histórico do Zpro</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Consulta o histórico de atendimentos direto no banco do Zpro, em <strong>somente leitura</strong>. Nada é importado:
        o contato é ligado pelo <code className="text-[11px]">id_zpro</code> informado na importação de contatos.
      </p>

      {seletorEmpresa}

      {!config.enabled && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
          O histórico do Zpro está <strong>desabilitado</strong> para esta empresa: ele não aparecerá nos atendimentos nem
          na lista de contatos. Ligue “Habilitar” e salve.
        </div>
      )}

      {!canEdit && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-4">
          Somente o SuperAdmin pode alterar esta configuração.
        </p>
      )}

      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="dark:text-white">Habilitar consulta de histórico do Zpro</Label>
            <p className="text-sm text-gray-500 dark:text-gray-400">Exibe o histórico do Zpro na tela de contatos</p>
          </div>
          <Switch
            checked={config.enabled}
            disabled={desabilitado}
            onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, enabled: checked }))}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="zproHost" className="dark:text-white">IP / Host do banco</Label>
            <Input id="zproHost" placeholder="82.29.57.200" value={config.host} onChange={set('host')} className={inputClass} disabled={desabilitado} />
          </div>
          <div>
            <Label htmlFor="zproPort" className="dark:text-white">Porta</Label>
            <Input id="zproPort" type="number" placeholder="5432" value={config.port} onChange={set('port')} className={inputClass} disabled={desabilitado} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="zproDatabase" className="dark:text-white">Nome do banco</Label>
            <Input id="zproDatabase" placeholder="postgres" value={config.database} onChange={set('database')} className={inputClass} disabled={desabilitado} />
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="zproTenantId" className="dark:text-white">Cliente no Zpro (tenant)</Label>
              {canEdit && (
                <Button type="button" variant="outline" size="sm" onClick={buscarTenants} disabled={buscandoTenants || loading} className="dark:text-white dark:bg-slate-700 dark:hover:bg-slate-600">
                  {buscandoTenants ? 'Buscando...' : 'Buscar tenants'}
                </Button>
              )}
            </div>
            {tenants ? (
              <Select value={config.tenantId} onValueChange={(v) => setConfig((prev) => ({ ...prev, tenantId: v }))} disabled={desabilitado}>
                <SelectTrigger id="zproTenantId" className={inputClass}>
                  <SelectValue placeholder="Selecione o cliente do Zpro" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800">
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)} disabled={!!t.vinculadoA}>
                      {t.id} — {t.nome} ({t.contatos.toLocaleString('pt-BR')} contatos)
                      {t.vinculadoA ? ` · já vinculado a ${t.vinculadoA}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input id="zproTenantId" type="number" placeholder="1" value={config.tenantId} onChange={set('tenantId')} className={inputClass} disabled={desabilitado} />
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              O banco do Zpro guarda vários clientes. Use “Buscar tenants” para escolher o cliente pelo nome; cada tenant só pode estar em uma empresa.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="zproUser" className="dark:text-white">Usuário</Label>
            <Input id="zproUser" autoComplete="off" value={config.user} onChange={set('user')} className={inputClass} disabled={desabilitado} />
          </div>
          <div>
            <Label htmlFor="zproPassword" className="dark:text-white">Senha</Label>
            <Input id="zproPassword" type="password" autoComplete="new-password" placeholder="Senha do banco" value={config.password} onChange={set('password')} className={inputClass} disabled={desabilitado} />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Armazenada criptografada. Deixe como está para manter a senha atual.</p>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
          <Label className="dark:text-white">Mídias (opcional)</Label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
            Os arquivos são lidos por SFTP no mesmo servidor do banco ({config.host || 'IP acima'}). Sem a pasta, as mídias
            aparecem apenas como indicação.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="zproMediaPath" className="dark:text-white">Pasta das mídias no servidor</Label>
              <Input id="zproMediaPath" placeholder="/home/backups/sintis-midias" value={config.mediaPath} onChange={set('mediaPath')} className={inputClass} disabled={desabilitado} />
            </div>
            <div>
              <Label htmlFor="zproSshPort" className="dark:text-white">Porta SSH</Label>
              <Input id="zproSshPort" type="number" placeholder="22" value={config.sshPort} onChange={set('sshPort')} className={inputClass} disabled={desabilitado} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label htmlFor="zproSshUser" className="dark:text-white">Usuário SSH</Label>
              <Input id="zproSshUser" autoComplete="off" value={config.sshUser} onChange={set('sshUser')} className={inputClass} disabled={desabilitado} />
            </div>
            <div>
              <Label htmlFor="zproSshPassword" className="dark:text-white">Senha SSH</Label>
              <Input id="zproSshPassword" type="password" autoComplete="new-password" placeholder="Senha do usuário SSH" value={config.sshPassword} onChange={set('sshPassword')} className={inputClass} disabled={desabilitado} />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Armazenada criptografada. Use um usuário só com leitura na pasta.</p>
            </div>
          </div>
        </div>

        {resumo && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 text-sm text-emerald-800 dark:text-emerald-300">
            Conexão OK{resumo.tenant ? ` — cliente “${resumo.tenant}”` : ''} — {resumo.contatos.toLocaleString('pt-BR')} contatos, {resumo.tickets.toLocaleString('pt-BR')} tickets e{' '}
            {resumo.mensagens.toLocaleString('pt-BR')} mensagens neste tenant.
            {resumo.midias && (
              <p className={`mt-1 ${resumo.midias.ok ? '' : 'text-red-700 dark:text-red-400'}`}>
                Mídias: {resumo.midias.mensagem}
              </p>
            )}
          </div>
        )}

        {canEdit && (
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={testar} disabled={testing || loading} className="dark:text-white dark:bg-slate-700 dark:hover:bg-slate-600">
              {testing ? 'Testando...' : 'Testar conexão'}
            </Button>
            <Button type="button" onClick={salvar} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ZproHistoricoConfig;
